import React, { useEffect, useMemo } from "react";

import { Alert, AlertDescription, AlertIcon } from "@chakra-ui/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  StatisticsEmptyState,
} from "../Statistics/components/statisticsEmptyState";
import {
  UniversityCourseCard,
  type UniversityCourseCardModel,
} from "./UniversityCourseCard";

export type UniversityCoursesTab = "enrolled" | "all" | "completed";

type UniversityCoursesTabsProps = {
  enrolledCourses: UniversityCourseCardModel[];
  allCourses: UniversityCourseCardModel[];
  completedCourses: UniversityCourseCardModel[];
  coursesTab: UniversityCoursesTab;
  onCoursesTabChange: (tab: UniversityCoursesTab) => void;
  coursesError: string | null;
  onOpenCourse: (sanityId: string, courseTitle?: string) => void;
};

export function UniversityCoursesTabs({
  enrolledCourses,
  allCourses,
  completedCourses,
  coursesTab,
  onCoursesTabChange,
  coursesError,
  onOpenCourse,
}: UniversityCoursesTabsProps): React.ReactElement {
  const showCompletedTab = completedCourses.length > 0;
  const hasEnrolledCourses = enrolledCourses.length > 0;

  useEffect(() => {
    if (coursesTab === "completed" && !showCompletedTab) {
      onCoursesTabChange(hasEnrolledCourses ? "enrolled" : "all");
    }
  }, [coursesTab, hasEnrolledCourses, onCoursesTabChange, showCompletedTab]);

  const gridClassName = useMemo(
    () =>
      "grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3",
    [],
  );

  return (
    <div data-university-courses-tabs="" className="w-full min-w-0">
      {coursesError ? (
        <Alert status="error" borderRadius="12px" mb="16px">
          <AlertIcon />
          <AlertDescription>{coursesError}</AlertDescription>
        </Alert>
      ) : null}

      <Tabs
        data-trade-log-tabs=""
        data-university-tabs=""
        className="w-full"
        value={coursesTab}
        onValueChange={(value) => onCoursesTabChange(value as UniversityCoursesTab)}
      >
        <div className="mb-4 overflow-x-auto overflow-y-hidden">
          <TabsList className="min-w-max">
            {hasEnrolledCourses ? (
              <>
                <TabsTrigger value="enrolled" title="Enrolled courses">
                  Enrolled
                </TabsTrigger>
                <TabsTrigger value="all" title="All courses">
                  All courses
                </TabsTrigger>
              </>
            ) : (
              <>
                <TabsTrigger value="all" title="All courses">
                  All courses
                </TabsTrigger>
                <TabsTrigger value="enrolled" title="Enrolled courses">
                  Enrolled
                </TabsTrigger>
              </>
            )}
            {showCompletedTab ? (
              <TabsTrigger value="completed" title="Completed courses">
                Completed
              </TabsTrigger>
            ) : null}
          </TabsList>
        </div>

        <TabsContent value="enrolled" className="mt-0">
          {enrolledCourses.length === 0 ? (
            <StatisticsEmptyState
              variant="no-data"
              title="No enrolled courses"
              description="You haven't enrolled in any courses yet."
            />
          ) : (
            <div className={gridClassName}>
              {enrolledCourses.map((course) => (
                <UniversityCourseCard
                  key={course.sanityId ?? course.id}
                  course={course}
                  variant="enrolled"
                  onPrimaryAction={() => {
                    if (course.sanityId) onOpenCourse(course.sanityId, course.title);
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="mt-0">
          {allCourses.length === 0 && !coursesError ? (
            <StatisticsEmptyState
              variant="no-data"
              title="No courses available"
              description="Check back later for new courses."
            />
          ) : (
            <div className={gridClassName}>
              {allCourses.map((course) => (
                <UniversityCourseCard
                  key={course.sanityId ?? course.id}
                  course={course}
                  variant="catalog"
                  onPrimaryAction={() => {
                    if (course.sanityId) onOpenCourse(course.sanityId, course.title);
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {showCompletedTab ? (
          <TabsContent value="completed" className="mt-0">
            <div className={gridClassName}>
              {completedCourses.map((course) => (
                <UniversityCourseCard
                  key={course.sanityId ?? course.id}
                  course={course}
                  variant="completed"
                  onPrimaryAction={() => {
                    if (course.sanityId) onOpenCourse(course.sanityId, course.title);
                  }}
                />
              ))}
            </div>
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}
